import {
  walk,
  getParent
} from './treeUtils'

/**
 * Creates a tree for purely presentational purposes.
 * Structure is identical to the passed tree, but is has none of its data,
 * instead nodes have properties for state/display:
 *
 *   {
 *      path       : [1,2]       // Its "address", an array of indices in the parent chain
 *      slot       : 0           // Slot position, or 'vertical index', within parent's visible nodes
 *      globalSlot : 0           // Global slot within all visible nodes
 *      isShown    : true|false  // Visible or not
 *      isParent   : true|false  // Has children
 *      isOpen     : true|false  // If it has children, opened/closed state
 *   }
 *
 * @param {object} tree
 */
const createShadowTree = (tree) => {
  const shadowTree = []

  walk(tree, (node, path, depth, count) => {
    const isParent = Boolean(node.children)

    const shadowNode = {
      path,
      isParent,
      id: count,
      isShown: false
    }

    if (isParent) {
      shadowNode.isOpen = false
      shadowNode.children = []
    }

    (depth === 0)
      ? shadowTree.push(shadowNode)
      : getParent(shadowTree, path).children.push(shadowNode)
  })

  return shadowTree
}

/**
 * Updates slots.
 * To be used if children are nested inside parents (positions relative to parents)
 *
 * @param tree         {Array}   - Tree structure
 * @param changedNode  {Object}  - Parent node that was opened/closed
 * @returns            {Number}  - Number of nodes shown
 */
const updateSlots = (tree, changedNode, global = { slot: 0 }, localSlotStart = 0) => {
  let localSlot = localSlotStart

  tree.forEach((node) => {
    if (!node.isShown) return

    node.globalSlot = global.slot++

    node.slot = localSlot

    localSlot += (node.isParent && node.isOpen)
      ? updateSlots(node.children, changedNode, global, 1)
      : 1
  })

  return localSlot
}

const updateBranchVisibility = (shadowTree, branch) => {
  const { isShown } = branch

  walk(branch.children, node => {
    if (isShown) { // If open children in sub folders are shown only if parent is open
      node.isShown = getParent(shadowTree, node.path).isOpen
    } else { // If closed all children are hidden
      node.isShown = false
    }
  })
}

const toggleNodeIsOpen = (shadowTree, node) => {
  node.isOpen = !node.isOpen

  updateBranchVisibility(shadowTree, node)
  updateSlots(shadowTree, node)
}

export {
  createShadowTree,
  toggleNodeIsOpen,
  updateSlots,
  updateBranchVisibility
}
